/**
 * Shopify Product Input Service
 * 
 * This service handles the preparation and validation of product data
 * before it is sent to the Shopify API. It ensures all inputs follow
 * the Shopify Admin API 2025-01 specifications.
 */

export class ShopifyProductInputService {
  /**
   * Prepares the product input for the Shopify API
   * @param {object} product - Raw product data
   * @param {object} options - Additional options for product creation
   * @returns {object} Formatted product input
   */
  prepareProductInput(product, options = {}) {
    const input = {
      title: this.sanitizeString(product.title),
      descriptionHtml: this.sanitizeString(product.descriptionHtml || product.bodyHtml || product.html),
      status: product.status || 'ACTIVE',
      productType: this.sanitizeString(product.productType),
      vendor: this.sanitizeString(product.vendor),
      options: this.formatProductOptions(product.options),
      seo: this.prepareSEOInput(product),
      privateMetafields: this.preparePrivateMetafields(product.privateMetafields),
      metafields: this.prepareMetafields(product.metafields),
      giftCard: product.giftCard || false
    };

    // Handle optional fields
    if (product.handle) {
      input.handle = this.sanitizeString(product.handle);
    }

    if (product.tags) {
      input.tags = this.formatTags(product.tags);
    }

    return input;
  }

  /**
   * Formats product options according to API requirements
   * @param {Array} options - Raw product options
   * @returns {Array} Formatted options
   */
  formatProductOptions(options = []) {
    if (!Array.isArray(options)) {
      return [];
    }

    return options.map((option, index) => ({
      name: this.sanitizeString(option.name),
      position: index + 1,
      values: Array.isArray(option.values) 
        ? option.values.map(value => this.sanitizeString(value))
        : []
    }));
  }

  /**
   * Formats variant option values according to Shopify API 2025-01
   * @param {Array} options - Array of option values
   * @param {Array} productOptions - Product option definitions
   * @returns {Array} Formatted option values
   */
  formatVariantOptions(options = [], productOptions = []) {
    if (!Array.isArray(options) || !Array.isArray(productOptions)) {
      return [];
    }

    return options.map((value, index) => ({
      optionName: productOptions[index]?.name || '',
      name: this.sanitizeString(value)
    }));
  }

  /**
   * Prepares variant input for the Shopify API
   * @param {object} variant - Raw variant data
   * @param {Array} productOptions - Product option definitions
   * @returns {object} Formatted variant input
   */
  prepareVariantInput(variant, productOptions = []) {
    const input = {
      price: variant.price ? variant.price.toString() : '0.00',
      compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice.toString() : null,
      barcode: this.sanitizeString(variant.barcode),
      sku: this.sanitizeString(variant.sku),
      taxable: variant.taxable !== false,
      inventoryPolicy: variant.inventory_policy || variant.inventoryPolicy || 'DENY',
      requiresShipping: variant.requiresShipping !== false,
      weight: variant.weight ? parseFloat(variant.weight) : undefined,
      weightUnit: variant.weightUnit || 'KILOGRAMS'
    };

    // Format option values according to API 2025-01
    if (variant.options) {
      input.optionValues = this.formatVariantOptions(variant.options, productOptions);
    }

    // Add inventory item if SKU is present
    if (variant.sku) {
      input.inventoryItem = {
        sku: this.sanitizeString(variant.sku)
      };
    }

    return input;
  }

  /**
   * Prepares SEO input for the product
   * @param {object} product - Product data
   * @returns {object} Formatted SEO input
   */
  prepareSEOInput(product) {
    const seo = {
      title: this.sanitizeString(product.seoTitle || product.title),
      description: this.sanitizeString(product.seoDescription || product.descriptionHtml)
    };

    return seo;
  }

  /**
   * Prepares private metafields input
   * @param {Array} metafields - Raw private metafields
   * @returns {Array} Formatted private metafields
   */
  preparePrivateMetafields(metafields = []) {
    if (!Array.isArray(metafields)) {
      return [];
    }

    return metafields.map(metafield => ({
      key: this.sanitizeString(metafield.key),
      namespace: this.sanitizeString(metafield.namespace),
      valueType: metafield.valueType,
      value: this.formatMetafieldValue(metafield.value, metafield.valueType)
    }));
  }

  /**
   * Prepares public metafields input
   * @param {Array} metafields - Raw metafields
   * @returns {Array} Formatted metafields
   */
  prepareMetafields(metafields = []) {
    if (!Array.isArray(metafields)) {
      return [];
    }

    return metafields.map(metafield => ({
      key: this.sanitizeString(metafield.key),
      namespace: this.sanitizeString(metafield.namespace),
      type: metafield.type,
      value: this.formatMetafieldValue(metafield.value, metafield.type)
    }));
  }

  /**
   * Formats tags into the required array format
   * @param {string|Array} tags - Raw tags
   * @returns {Array} Formatted tags
   */
  formatTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map(tag => this.sanitizeString(tag));
    }
    if (typeof tags === 'string') {
      return tags.split(',').map(tag => this.sanitizeString(tag));
    }
    return [];
  }

  /**
   * Formats metafield values based on their type
   * @param {any} value - Raw value
   * @param {string} type - Metafield type
   * @returns {string} Formatted value
   */
  formatMetafieldValue(value, type) {
    if (value === null || value === undefined) {
      return '';
    }

    switch (type?.toLowerCase()) {
      case 'json':
      case 'json_string':
        return typeof value === 'string' ? value : JSON.stringify(value);
      case 'number_integer':
        return String(parseInt(value, 10));
      case 'number_decimal':
        return String(parseFloat(value));
      case 'boolean':
        return String(Boolean(value));
      case 'date':
      case 'date_time':
        return new Date(value).toISOString();
      default:
        return String(value);
    }
  }

  /**
   * Sanitizes a string value
   * @param {string} value - Value to sanitize
   * @returns {string} Sanitized value
   */
  sanitizeString(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  /**
   * Validates product input data
   * @param {object} input - Product input data
   * @returns {object} Validation result with errors if any
   */
  validateProductInput(input) {
    const errors = [];

    // Required fields
    if (!input.title) {
      errors.push('Product title is required');
    }

    // Validate options
    if (input.options) {
      const optionNames = new Set();
      input.options.forEach((option, index) => {
        if (!option.name) {
          errors.push(`Option ${index + 1} name is required`);
        }
        if (optionNames.has(option.name)) {
          errors.push(`Duplicate option name: ${option.name}`);
        }
        optionNames.add(option.name);

        if (!Array.isArray(option.values) || option.values.length === 0) {
          errors.push(`Option ${option.name} must have at least one value`);
        }
      });
    }

    // Validate metafields
    if (input.metafields) {
      input.metafields.forEach((metafield, index) => {
        if (!metafield.key || !metafield.namespace) {
          errors.push(`Metafield ${index + 1} must have both key and namespace`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validates variant input data
   * @param {object} input - Variant input data
   * @param {Array} productOptions - Product option definitions
   * @returns {object} Validation result with errors if any
   */
  validateVariantInput(input, productOptions = []) {
    const errors = [];

    // Validate option values format
    if (input.optionValues) {
      if (!Array.isArray(input.optionValues)) {
        errors.push('optionValues must be an array');
      } else {
        input.optionValues.forEach((optionValue, index) => {
          if (!optionValue.optionName) {
            errors.push(`Option ${index + 1} must have an optionName`);
          }
          if (!optionValue.name) {
            errors.push(`Option ${index + 1} must have a name value`);
          }
          if (productOptions[index] && optionValue.optionName !== productOptions[index].name) {
            errors.push(`Option ${index + 1} name mismatch: expected "${productOptions[index].name}", got "${optionValue.optionName}"`);
          }
        });
      }
    }

    // Validate numeric fields
    if (input.price && isNaN(parseFloat(input.price))) {
      errors.push('Price must be a valid number');
    }
    if (input.compareAtPrice && isNaN(parseFloat(input.compareAtPrice))) {
      errors.push('Compare at price must be a valid number');
    }
    if (input.weight && isNaN(parseFloat(input.weight))) {
      errors.push('Weight must be a valid number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
